import React from "react";
import { COLORS } from "../utils/constants";
import { LanguageCode } from "../utils/translations";
import BrandName from "../component/BrandName";

interface TermsProps {
  lang: LanguageCode;
}

const TermsOfService: React.FC<TermsProps> = ({ lang }) => {
  lang = "en";
  return (
    <div
      style={{
        backgroundColor: "#fff",
        color: COLORS.textDark,
        padding: "40px 6%",
        lineHeight: "1.8",
        minHeight: "100vh",
      }}
    >
      <div style={{ maxWidth: "950px", margin: "0 auto", textAlign: "left" }}>
        <header
          style={{
            borderBottom: `4px solid ${COLORS.brandPurple}`,
            marginBottom: "24px",
            paddingBottom: "20px",
          }}
        >
          <h1
            style={{
              color: COLORS.brandPurple,
              fontSize: "2.8rem",
              marginBottom: "10px",
              fontWeight: 800,
            }}
          >
            Service Agreements & Policies
          </h1>
          <p
            style={{
              fontSize: "1.2rem",
              color: COLORS.textMuted,
              fontWeight: 500,
            }}
          >
            Official Operating Guidelines for <BrandName />
          </p>
        </header>

        {/* --- INTRODUCTION --- */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>Introduction</h2>
          <p>
            Welcome to the official platform of <BrandName />, your dedicated
            Digital Marketing Agency Partner and Mentor. By accessing, browsing,
            or utilizing any of the digital services, tools, or consulting
            packages listed on this website, you acknowledge that you have read,
            understood, and expressly agree to be legally bound by the following
            Terms of Service, Privacy Policies, and Professional Conditions.
            These terms constitute a binding agreement between you (the Client)
            and <BrandName />.
          </p>
        </section>

        {/* --- SECTION 1 --- */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>📄 1. Terms of Service Agreement</h2>
          <p>
            All marketing services provided by <BrandName /> are developed for{" "}
            <strong>general business application</strong>. Our offerings are
            standardized to provide high-value outreach but are not pre-designed
            for specific industry verticals or unique niche categories.
          </p>
          <div style={subBox}>
            <p>
              <strong>1.1 General Nature of Services:</strong> Our core
              offerings—including SEO, Social Media Management, Meta Ads, Google
              Ads, and WhatsApp Marketing—are structured to support broad
              commercial objectives. They serve as foundational growth drivers
              rather than industry-specific custom software or unique technical
              builds.
            </p>
            <p>
              <strong>1.2 Custom Strategy Requirements:</strong> If your project
              demands a tailored solution, specialized platform integrations, or
              a combination of multiple complex features,{" "}
              <strong>
                <BrandName />
              </strong>{" "}
              strongly recommends booking a{" "}
              <strong>paid consultation session</strong>. This ensures your
              specific industry goals are evaluated with professional clarity
              before execution begins.
            </p>
            <p>
              <strong>1.3 Scope Boundaries:</strong> Each service is defined by
              specific deliverables. By engaging <BrandName />, you agree to
              abide by these defined boundaries. Any modification or extension
              of support beyond the agreed scope will require a secondary mutual
              agreement and may incur additional service charges.
            </p>
          </div>
        </section>

        {/* --- SECTION 2 --- */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>📄 2. Services & Features Disclaimer</h2>
          <p>
            <BrandName /> provides professional-grade tools and services
            designed to enhance digital presence. However, these services are
            general in nature and intended for businesses seeking basic to
            intermediate outreach.
          </p>
          <div style={subBox}>
            <p>
              <strong>2.1 No Performance Guarantees:</strong> While we utilize
              industry-best practices, <BrandName /> does not guarantee specific
              results or performance outcomes (such as exact ROI, lead counts,
              follower growth, or search rankings). Digital marketing is
              influenced by dynamic external factors, including competitor
              behavior and platform algorithm changes, which are beyond our
              control.
            </p>
            <p>
              <strong>2.2 Not a Strategic Roadmap:</strong> Standard listed
              services do not constitute a full-scale marketing strategy. For
              deep-dive funnel design, audience research, or high-level campaign
              execution, a dedicated <BrandName /> Strategic Consultation is
              required.
            </p>
          </div>
        </section>

        {/* --- SECTION 3 --- */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>
            📄 3. Privacy & Non-Disclosure Agreement (NDA)
          </h2>
          <p>
            Confidentiality is a cornerstone of the <BrandName /> partnership.
            Both the Client and <BrandName /> agree to protect all sensitive
            brand details, internal processes, and proprietary data shared
            during the engagement.
          </p>
          <div style={subBox}>
            <p>
              <strong>3.1 Mutual Protection:</strong> Neither party shall
              disclose confidential information—including business strategies,
              content plans, campaign data, or login credentials—to any third
              party without express written consent.
            </p>
            <p>
              <strong>3.2 Portfolio Rights:</strong> To showcase our
              professional capabilities, <BrandName /> reserves the right to
              display the Client's brand name, logo, and a general summary of
              services provided in our portfolio, case studies, and social media
              content. We will never share sensitive performance data or
              internal business metrics without approval.
            </p>
          </div>
        </section>

        {/* --- SECTION 4 --- */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>📄 4. Payment and Refund Policy</h2>
          <p>
            To maintain high-quality project execution and resource allocation,{" "}
            <BrandName /> operates under a strict financial policy.
          </p>
          <div style={subBox}>
            <p>
              <strong>4.1 100% Advance Payment:</strong> All service engagements
              require full advance payment to prioritize your project in our
              workflow, unless a milestone-based agreement is signed in writing.
            </p>
            <p>
              <strong>4.2 No-Refund Policy:</strong> All payments made to{" "}
              <BrandName /> are <strong>strictly non-refundable</strong>. Our
              services are sold based on time, expertise, and defined
              deliverables rather than subjective satisfaction or specific
              revenue milestones.
            </p>
          </div>
        </section>

        {/* --- SECTION 5 --- */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>📄 5. No-Poaching Agreement</h2>
          <p>
            In the spirit of professional respect, both parties agree to a
            strict no-poaching policy. Neither the Client nor <BrandName />{" "}
            shall attempt to hire, engage, or offer separate employment to the
            other party's team members, freelancers, or associates during the
            project period and for <strong>six months</strong> following its
            completion.
          </p>
        </section>
      </div>
    </div>
  );
};

// Reusable Styles
const sectionStyle: React.CSSProperties = { marginBottom: "24px" };
const headingStyle: React.CSSProperties = {
  color: COLORS.brandPurple,
  fontSize: "1.7rem",
  marginBottom: "20px",
  fontWeight: 700,
};
const subBox: React.CSSProperties = {
  padding: "16px",
  backgroundColor: "#f9fafb",
  borderRadius: "12px",
  borderLeft: `5px solid ${COLORS.brandTeal}`,
  marginTop: "15px",
};

export default TermsOfService;
